import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import StickyMobileCTA from "@/components/landing/StickyMobileCTA";
import { SERVICES } from "@/seo/content";
import { CALENDLY_URL, WHATSAPP_URL } from "@/i18n/translations";

const ServicePage = ({ slug }) => {
  const data = SERVICES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  const url = `https://maif.ma/${data.slug}`;
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.eyebrow,
    serviceType: data.eyebrow,
    provider: {
      "@type": "Organization",
      name: "Moroccan AI Factory",
      url: "https://maif.ma",
    },
    areaServed: ["Morocco", "Africa", "Worldwide"],
    url,
    description: data.description,
  };
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://maif.ma/" },
      { "@type": "ListItem", position: 2, name: data.eyebrow, item: url },
    ],
  };

  return (
    <div className="bg-maif text-maif min-h-screen">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.description} />
        <meta name="keywords" content={data.keywords.join(", ")} />
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="en" href={url} />
        <link rel="alternate" hrefLang="fr" href={url} />
        <link rel="alternate" hrefLang="ar" href={url} />
        <link rel="alternate" hrefLang="x-default" href={url} />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Moroccan AI Factory" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.title} />
        <meta name="twitter:description" content={data.description} />
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <Header />
      <main className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-maif-muted hover:text-maif transition mb-8"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>

          <div className="eyebrow">{data.eyebrow}</div>
          <h1 className="mt-4 font-display font-light text-4xl md:text-6xl tracking-tighter text-maif leading-[1.05]">
            {data.h1}
          </h1>

          <div className="mt-12 space-y-12">
            {data.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight text-maif">
                  {s.h2}
                </h2>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-maif-muted">
                  {s.body}
                </p>
              </section>
            ))}
          </div>

          {/* CTA card */}
          <div className="mt-16 rounded-2xl bg-maif-2 p-8 md:p-10 border border-sand-soft">
            <h2 className="font-display text-2xl md:text-3xl text-maif tracking-tight">
              Ready to start?
            </h2>
            <p className="mt-3 text-maif-muted">
              Free 30-min discovery call. We'll map your workflow and surface the highest-leverage automations — no commitment.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/#lead" className="btn-primary justify-center">
                Get my AI audit <ArrowUpRight size={16} />
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary justify-center"
              >
                Talk on WhatsApp
              </a>
            </div>
          </div>

          {/* FAQs */}
          <section className="mt-20">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-maif">
              Frequently asked questions
            </h2>
            <div className="mt-8 divide-y divide-sand-soft border-y border-sand-soft">
              {data.faqs.map((f, i) => (
                <details key={i} className="py-6 group">
                  <summary className="cursor-pointer font-display text-lg md:text-xl text-maif tracking-tight list-none flex items-center justify-between gap-4">
                    {f.q}
                    <span className="text-terracotta group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-4 text-maif-muted leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Related services */}
          {data.related && data.related.length > 0 && (
            <section className="mt-20">
              <div className="eyebrow">Continue exploring</div>
              <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight text-maif">
                Related services
              </h2>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {data.related.map((r) => {
                  const rs = SERVICES[r];
                  if (!rs) return null;
                  return (
                    <Link
                      key={r}
                      to={`/${r}`}
                      className="block rounded-xl bg-maif-2 border border-sand-soft p-5 hover:border-sand-mid transition"
                    >
                      <div className="eyebrow text-[10px]">{rs.eyebrow}</div>
                      <div className="mt-2 font-display text-maif">{rs.h1.split(" — ")[0]}</div>
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-terracotta">
                        Learn more <ArrowUpRight size={12} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default ServicePage;
